import { CheckboxCard as ChakraCheckboxCard, Flex } from "@chakra-ui/react";
import { forwardRef } from "react";

/**
 * FilterChipGroup — a single-line row of compact toggle pills for multi-select
 * filtering. The multi-select counterpart to `RadioCardGroup`: each chip is sized
 * to its own label rather than stretching, so a handful of them read as one quiet
 * line above a list.
 *
 * Built on Chakra's `CheckboxCard` in its **no-indicator** form (Root → HiddenInput
 * → Control → Label, with `Indicator` deliberately omitted): the whole pill is the
 * affordance, so a separate tick box would just add weight at this size. Checked /
 * hover / focus-visible / disabled states all come from the `surface` recipe, so
 * this file styles nothing that Chakra already owns.
 *
 * Use for list filters where several buckets can be on at once. For a single
 * choice use `RadioCardGroup`; for one consequential boolean use `SegmentedSetting`;
 * for a full-width option tile with a visible tick use this folder's `CheckboxCard`.
 *
 * Keyboard + screen-reader behaviour comes from `HiddenInput` — each chip is a real
 * checkbox, tabbable and toggled with Space.
 */
export interface FilterChipOption<T extends string = string> {
  id: T;
  label: string;
}

export interface FilterChipGroupProps<T extends string = string> {
  /** Currently selected ids. Empty array = nothing selected. */
  value: T[];
  /** Called with the toggled id and the state it is moving to. */
  onChange: (id: T, next: boolean) => void;
  options: FilterChipOption<T>[];
  disabled?: boolean;
}

function FilterChipGroupInner<T extends string = string>(
  { value, onChange, options, disabled }: FilterChipGroupProps<T>,
  ref: React.Ref<HTMLDivElement>,
) {
  return (
    <Flex ref={ref} gap={2} flexWrap="wrap" alignItems="center">
      {options.map((opt) => {
        const active = value.includes(opt.id);
        return (
          <ChakraCheckboxCard.Root
            key={opt.id}
            checked={active}
            // Typed structurally: Chakra's re-exported CheckboxCardCheckedChangeDetails
            // does not resolve through to the Ark type, so there is no contextual type
            // to infer from. `checked` is boolean | "indeterminate" — only an explicit
            // true counts as on.
            onCheckedChange={(details: {
              checked: boolean | "indeterminate";
            }) => onChange(opt.id, details.checked === true)}
            disabled={disabled}
            size="sm"
            variant="surface"
            colorPalette="blue"
            align="center"
            justify="center"
            // The recipe's root base is `flex: 1`, which would stretch every chip to
            // an equal share of the row. A chip is only as wide as its own label.
            w="auto"
            flex="0 0 auto"
            // `control` inherits this, so the pill shape is set once here.
            borderRadius="full"
          >
            <ChakraCheckboxCard.HiddenInput />
            <ChakraCheckboxCard.Control px={3} py={1}>
              <ChakraCheckboxCard.Label
                fontSize="12px"
                fontWeight={500}
                whiteSpace="nowrap"
              >
                {opt.label}
              </ChakraCheckboxCard.Label>
            </ChakraCheckboxCard.Control>
          </ChakraCheckboxCard.Root>
        );
      })}
    </Flex>
  );
}

export const FilterChipGroup = forwardRef(FilterChipGroupInner) as <
  T extends string = string,
>(
  props: FilterChipGroupProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => ReturnType<typeof FilterChipGroupInner>;

export default FilterChipGroup;
